import React from 'react';

import ScrollableIsiFooter from './ScrollableIsiFooter';
import FooterLines from './FooterLines';
import customClientConfig from '../../../customClientConfig';

const maciVericelFooterLogo = require('../../../assets/maci/maci_vericel_footer_logo.png');
const maciButtonExpanded = require('../../../assets/maci/maci_button_expanded.png');
const maciButtonCollapsed = require('../../../assets/maci/maci_button_collapsed.png');

const Rmark = () => <sup style={{ verticalAlign: 'super', fontSize: '0.6em' }}>®</sup>;

const column1Content = (
  <>
    <p>
      <strong>INDICATION</strong>
    </p>
    <p>
      MACI
      <Rmark /> (autologous cultured chondrocytes on porcine collagen membrane) is an autologous
      cellularized scaffold product indicated for the repair of single or multiple symptomatic,
      full-thickness cartilage defects of the knee with or without bone involvement in adults.
    </p>
    <p>
      <strong>Limitations of Use</strong>
    </p>
    <p>Effectiveness of MACI in joints other than the knee has not been established.</p>
    <p>
      Safety and effectiveness of MACI in patients over the age of 55 years have not been
      established.
    </p>
  </>
);

const column2Content = (
  <>
    <p>
      <strong>IMPORTANT SAFETY INFORMATION</strong>
    </p>
    <p>
      <strong>Contraindications:</strong> MACI
      <Rmark /> is contraindicated in patients with a known history of hypersensitivity to
      gentamicin, other aminoglycosides, products of porcine or bovine origin; in patients with
      severe osteoarthritis of the knee, inflammatory arthritis, inflammatory joint disease, or
      uncorrected congenital blood coagulation disorders; in patients who have undergone prior knee
      surgery in the past 6 months, excluding surgery to procure a biopsy or a concomitant procedure
      to prepare the knee for a MACI implant; or in patients unable to cooperate with a
      physician-prescribed post-surgical rehabilitation program.
    </p>
    <p>
      <strong>Warnings and Precautions:</strong>
    </p>
    <ul style={{ paddingLeft: 16, margin: '0 0 10px 0' }}>
      <li>
        Malignancy: The risk of MACI in patients with malignancy in the area of cartilage biopsy or
        implant is unknown. Expansion of malignant or dysplastic cells present in biopsy tissue
        during manufacture and subsequent implantation may be possible.
      </li>
      <li>
        Transmissible infectious diseases: Because patients undergoing procedures associated with
        MACI are not routinely tested for transmissible infectious diseases, cartilage biopsy and
        MACI implant may carry risk of transmitting infectious diseases.
      </li>
      <li>
        Presurgical Comorbidities: Local inflammation or active infection in the bone, joint, and
        surrounding soft tissue, meniscal pathology, cruciate ligament instability, and misalignment
        should be assessed and treated prior to or concurrent with MACI implantation.
      </li>
      <li>
        Product Sterility: Final sterility test results are not available at the time of shipping.
      </li>
    </ul>
    <p>
      <strong>Adverse Reactions:</strong> The most frequently occurring adverse reactions reported
      for MACI (≥5%) were arthralgia, tendonitis, back pain, joint swelling, and joint effusion.
      Serious adverse reactions reported for MACI were arthralgia, cartilage injury, meniscus
      injury, treatment failure, and osteoarthritis.
    </p>
    <p>
      <strong>Specific Populations:</strong>
    </p>
    <ul style={{ paddingLeft: 16, margin: '0 0 10px 0' }}>
      <li>
        Use of MACI in pediatric patients (younger than 18 years of age) or in patients over 65
        years of age has not been established.
      </li>
      <li>
        The MACI implant is not recommended during pregnancy. For implantations post-pregnancy, the
        safety of breastfeeding to an infant has not been determined.
      </li>
    </ul>
    <p>
      To report negative side effects, contact Vericel Corporation at 1-800-453-6948 or FDA at
      1-800-FDA-1088 (1-800-332-1088) or{' '}
      <a
        href="https://www.fda.gov/medwatch"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: 'inherit', textDecoration: 'underline', textDecorationColor: '#aaaaaa' }}
      >
        www.fda.gov/medwatch
      </a>
      .
    </p>
    <p>
      <strong>
        Please see Full{' '}
        <a
          style={{ color: '#000000', textDecoration: 'underline' }}
          href="https://www.maci.com/pdf/maci-full-prescribing-information.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          Prescribing Information
        </a>
        .
      </strong>
    </p>
  </>
);

const isiContent = (
  <div className="isi-content-wrapper">
    <div className="isi-columns">
      <div className="isi-col isi-col-1">{column1Content}</div>
      <div className="isi-col isi-col-2" style={{ paddingBottom: 20 }}>
        {column2Content}
      </div>
    </div>
  </div>
);

const IsiCompanyFooter = ({ footerLines }) => (
  <div className="isi-company-footer">
    <img src={maciVericelFooterLogo} alt="Vericel" style={{ maxHeight: 56, flexShrink: 0 }} />
    <div>
      <FooterLines lines={footerLines} lineStyle={{ margin: '6px 0', fontSize: 14 }} />
    </div>
  </div>
);

const MaciFooterComponent = ({ tenant }) => {
  const { footerLines } = customClientConfig[tenant];

  return (
    <ScrollableIsiFooter
      companyFooter={<IsiCompanyFooter footerLines={footerLines} />}
      config={{
        ariaLabel: 'MACI Important Safety Information',
        className: 'isi-two-col',
        collapsedContent: isiContent,
        expandedContent: isiContent,
        includeCollapsedInExpanded: false,
        hideHeader: true,
        collapsedHeight: 150,
        expandedHeight: 'screen',
        footerHeight: 80,
        spacerExtra: 60,
        // ISI height tracks page scroll directly — no discrete expand/collapse.
        // The user scrolls into the expansion zone at the page bottom to open.
        scrollLinked: true,
        expandButton: <img src={maciButtonCollapsed} alt="Show ISI" style={{ maxHeight: 22 }} />,
        collapseButton: <img src={maciButtonExpanded} alt="Hide ISI" style={{ maxHeight: 22 }} />,
        footerOnlyWhenExpanded: true,
        footerStyle: {
          borderBottom: '5px solid #FFD100',
          height: 'auto',
          alignItems: 'center',
          padding: '10px 24px'
        }
      }}
    />
  );
};

export default MaciFooterComponent;
